<template>
  <v-card width="100%" class="mx-auto">
    <v-card-title>New Student</v-card-title>
    <v-card-text>
      <v-form ref="studentForm" v-model="valid" lazy-validation>
        <div class="text-center text-h5" v-if="sibling">
          Sibling: {{ sibling.firstName }} {{ sibling.lastName }}
        </div>
        <v-select
          :items="centers"
          v-model="center"
          label="Location"
          item-text="name"
          item-value="id"
          :rules="[rules.required]"
        />
        <v-select
          v-if="contactTypes && contactTypes.length > 1"
          label="Type"
          v-model="student.contactTypeId"
          :items="contactTypes"
          item-text="name"
          item-value="id"
        />
        <v-text-field
          v-model="student.firstName"
          label="First Name"
          :required="true"
          :rules="[rules.required]"
        />
        <v-text-field
          v-model="student.lastName"
          label="Last Name"
          :required="true"
          :rules="[rules.required]"
        />
        <v-text-field
          v-model="student.email"
          label="Email"
          :rules="[rules.emailMatch]"
          clearable
        />
        <vue-tel-input
          ref="telInput"
          @validate="validPhone"
          v-model="phone"
          :autoDefaultCountry="true"
          :autoFormat="true"
          :validCharactersOnly="true"
          :inputOptions="{ placeholder: 'Enter a phone number' }"
        >
          <template v-slot:arrow-icon="">
            <v-icon> mdi-menu-down </v-icon>
          </template>
        </vue-tel-input>
        <date-picker
          label="Date of Birth"
          :defaultNowOnNull="false"
          :show-hint="false"
          :show-icon="false"
          v-model="student.dob"
          :outlined="false"
          :rules="[...rules.dob]"
          :datePattern="dateFormat"
          clearable
        />
        <v-text-field
          v-model="student.clientStudentId"
          label="Student ID (Optional)"
          :required="true"
        />
        <v-select
          v-model="student.gender"
          :items="genderOptions"
          label="Gender"
          deletable-chips
          chips
        ></v-select>
        <v-select
          v-if="grades && grades.length > 0"
          v-model="student.grade"
          :items="grades"
          label="Grade"
          deletable-chips
          chips
        ></v-select>
        <v-textarea v-model="student.memo" label="Memo" :rows="2" auto-grow />
        <!-- <v-switch v-model="student.active" label="Active" /> -->
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn @click="close">Cancel</v-btn>
      <v-btn @click="saveStudent" color="primary" class="mx-5">Save</v-btn>
      <v-spacer />
    </v-card-actions>
  </v-card>
</template>

<script>
import { getStudent } from "@/services/student";
import { getAllContactTypes } from "@/services/contactType";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import { rules as globalRules } from "@/store/util/rules.js"; // Importing with a different name
import { VueTelInput } from "vue-tel-input";
import "vue-tel-input/dist/vue-tel-input.css";

export default {
  props: {
    centers: { type: Array, required: true },
    siblingId: { type: Number, required: false },
  },
  data() {
    return {
      valid: false,
      phoneInput: null,
      contactTypes: [],
      sibling: null,
      genderOptions: ["Female", "Male"],
      grades: [],
      student: {
        firstName: "",
        lastName: "",
        gender: "",
        grade: "",
        email: "",
        dob: "",
        memo: "",
        clientStudentId: "",
        active: null,
        contactId: null,
        centerId: null,
        contactTypeId: null,
      },
      dateFormat: "YYYY-MM-DD",
      rules: {
        ...globalRules, // Spreading global rules
        dob: [
          (v) => {
            if (!v) return true;
            let isValidFormat = moment(v, this.dateFormat, true).isValid(); // The 'true' argument is for strict parsing
            if (!isValidFormat) {
              return `Date of birth should be in ${this.dateFormat} format`;
            }
            const today = moment();
            const birthDate = moment(v, this.dateFormat);
            return (
              birthDate.isSameOrBefore(today) ||
              "Date of birth cannot be in the future"
            );
          },
        ],
      },
    };
  },
  mounted() {
    if (this.centers && this.centers.length == 1) {
      this.center = this.centers[0].id;
    } else if (this.globalCenter && this.globalCenter.id) {
      this.center = this.globalCenter.id;
    }

    if (this.siblingId) {
      this.initSibling();
    }

    this.setDependencies();
  },
  computed: {
    ...mapGetters(["isAppLoading", "myCenters", "globalCenter"]),
  },
  methods: {
    ...mapActions(["addMessage", "addErrorMessage", "addSuccessMessage"]),
    ...mapActions("grade", ["fetchGrades"]),
    ...mapActions("student", ["createStudent"]),
    validPhone(phone) {
      this.phoneInput = phone;
    },
    async saveStudent() {
      if (!this.$refs.studentForm.validate()) return;

      let student = this.student;
      if (this.phoneInput !== null && this.phoneInput.nationalNumber && !this.phoneInput.valid) {
        this.addErrorMessage("Invalid phone number.");
        return;
      } else {
        student.phone = this.phoneInput.nationalNumber;
        student.phoneCountryCode = this.phoneInput.countryCode;
      }


      if (this.sibling) {
        student.contactId = this.sibling.contactId;
      }

      if (!student.contactTypeId && this.contactTypes.length > 0) {
        student.contactTypeId = this.contactTypes[0].id;
      }
      student.active = true; // default student to active
      this.createStudent(student)
        .then((savedStudent) => {
          if (savedStudent) {
            this.addSuccessMessage("New student added");
            this.$emit("submit", savedStudent);
          } else {
            this.addErrorMessage("Error saving new student");
          }
        })
        .catch((error) => {
          const resp = error.data;
          if (resp.code == "RECORD_ALREADY_EXIST") {
            this.addErrorMessage(resp.message || "Student already existed");
          } else {
            this.addErrorMessage(resp.message || "Error saving student");
          }
        });
    },
    close() {
      this.$emit("close");
    },

    initSibling() {
      if (this.siblingId) {
        getStudent(this.siblingId).then((response) => {
          this.sibling = response.data;
        });
      }
    },
    setDependencies() {
      const params = {
        page: 0,
        size: 100,
      };
      getAllContactTypes(params).then((response) => {
        const allTypes = response.data.content;
        this.contactTypes = allTypes.filter((type) => type.has_student);
      });

      this.fetchGrades(params)
        .then((response) => {
          if (response) {
            const grades = response.content;
            if (grades && grades.length > 0) {
              this.grades = grades.map((g) => g.name);
            }
          }
        })
        .catch((error) => {
          console.log(error);
          this.addErrorMessage("Error retrieving grades");
        });
    },
  },
  components: { VueTelInput },
};
</script>
